import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Cumulative Projects Across All TLs Chart
 * Dual area/line chart showing Total Projects and Completed Projects
 */
const MBFCumulativeProjectsChart = ({ data = {}, isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default data - Adjusted to ensure Total >= Completed and to look like the image
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const defaultTotalProjects = [50, 47, 70, 90, 38, 50, 33, 65, 100, 45, 23, 87];
    const defaultCompletedProjects = [46, 22, 55, 40, 12, 40, 11, 35, 68, 42, 5, 32];

    const totalProjects = data.total || defaultTotalProjects;
    const completedProjects = data.completed || defaultCompletedProjects;

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: months,
                    datasets: [
                        {
                            label: 'Total projects',
                            data: totalProjects,
                            borderColor: '#D53184', // Highlighted pink from image
                            backgroundColor: 'rgba(213, 49, 132, 0.15)', // Light pink fill
                            borderWidth: 1.5,
                            fill: true,
                            tension: 0, // Angular lines
                            pointRadius: 0,
                            pointHoverRadius: 6,
                            pointHoverBackgroundColor: '#D53184',
                            pointHoverBorderColor: '#fff',
                            pointHoverBorderWidth: 2,
                            order: 2,
                        },
                        {
                            label: 'Completed Projects',
                            data: completedProjects,
                            borderColor: '#1B75BB', // Blue from image
                            backgroundColor: 'rgba(27, 117, 187, 0.1)', // Light blue fill
                            borderWidth: 1.5,
                            fill: true,
                            tension: 0, // Angular lines
                            pointRadius: 0,
                            pointHoverRadius: 6,
                            pointHoverBackgroundColor: '#1B75BB',
                            pointHoverBorderColor: '#fff',
                            pointHoverBorderWidth: 2,
                            order: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.98)',
                            titleColor: '#333333',
                            bodyColor: '#444444',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 4,
                            callbacks: {
                                label: function (context) {
                                    return ` ${context.dataset.label}: ${context.parsed.y}`;
                                }
                            }
                        },
                        datalabels: {
                            display: false,
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: true, color: '#444444' }, // Darker border like Image 2
                            ticks: {
                                color: '#444444',
                                font: {
                                    family: "'Inter', 'Arial', sans-serif",
                                    size: fontSize,
                                },
                            },
                        },
                        y: {
                            min: 0,
                            ticks: {
                                precision: 0,
                                color: '#444444',
                                font: {
                                    family: "'Inter', 'Arial', sans-serif",
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: {
                                color: '#eeeeee', // Lighter grid lines
                            },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, totalProjects, completedProjects]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '380px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: "'Inter', 'Arial', sans-serif",
                            fontSize: '16px',
                            fontWeight: 600,
                        }}
                    >
                        Cumulative Projects Across All TLs
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        '&:hover': { bgcolor: '#f5f5f5' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #eeeeee',
                        padding: '16px 12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        minHeight: 0,
                    }}
                >
                    {/* Y-Axis Label */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: "'Inter', 'Arial', sans-serif",
                                fontSize: '13px',
                                fontWeight: 500,
                            }}
                        >
                            No. of Projects
                        </Typography>
                    </Box>

                    {/* Chart + Controls */}
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            minHeight: 0,
                        }}
                    >
                        {/* Chart */}
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <canvas ref={chartRef} />
                        </Box>

                        {/* X-Axis Label */}
                        <Typography
                            sx={{
                                color: '#333333',
                                fontFamily: "'Inter', 'Arial', sans-serif",
                                fontSize: '13px',
                                fontWeight: 500,
                                textAlign: 'center',
                            }}
                        >
                            Months
                        </Typography>

                        {/* Legend */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '20px',
                                flexShrink: 0,
                                mt: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Box
                                    sx={{
                                        width: '24px',
                                        height: '14px',
                                        bgcolor: 'rgba(213, 49, 132, 0.15)',
                                        border: '1px solid #D53184',
                                        borderRadius: '2px',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: '#444444',
                                        fontFamily: "'Inter', 'Arial', sans-serif",
                                        fontSize: '12px',
                                    }}
                                >
                                    Total projects
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Box
                                    sx={{
                                        width: '24px',
                                        height: '14px',
                                        bgcolor: 'rgba(27, 117, 187, 0.1)',
                                        border: '1px solid #1B75BB',
                                        borderRadius: '2px',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: '#444444',
                                        fontFamily: "'Inter', 'Arial', sans-serif",
                                        fontSize: '12px',
                                    }}
                                >
                                    Completed Projects
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFCumulativeProjectsChart;
